import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Store, Package, ClipboardList, Users, ImagePlus, Save, Settings2, ContactRound, Truck, Printer } from "lucide-react";

const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const STATUS_LABEL = { pending: "Menunggu Konfirmasi", diproses: "Diproses", selesai: "Selesai", batal: "Batal" };
const STATUS_CLS = { pending: "bg-amber-100 text-amber-800", diproses: "bg-blue-100 text-blue-800", selesai: "bg-emerald-100 text-emerald-800", batal: "bg-red-100 text-red-700" };

function ProductsTab() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({});
  const load = () => api.get("/products").then(({ data }) => setRows(data.filter((p) => p.kind === "produk_jadi")));
  useEffect(() => { load(); }, []);
  const f = (id, k, v) => setForm((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));
  const val = (p, k) => (form[p.id] || {})[k] ?? (k === "online" ? !!p.online : k === "online_price" ? (p.online_price ?? p.price ?? 0) : (p.online_desc ?? ""));

  const save = async (p) => {
    const t = toast.loading("Menyimpan...");
    try {
      const fd = new FormData();
      fd.append("online", val(p, "online") ? "true" : "false");
      fd.append("online_price", val(p, "online_price"));
      fd.append("online_desc", val(p, "online_desc"));
      const photo = (form[p.id] || {}).photo;
      if (photo) fd.append("photo", photo);
      await api.post(`/store/products/${p.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(val(p, "online") ? `${p.name} tampil di toko online` : `${p.name} disembunyikan dari toko`, { id: t });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  return (
    <div className="space-y-3">
      {rows === null && <p className="text-sm text-slate-500">Memuat...</p>}
      {(rows || []).map((p) => (
        <div key={p.id} data-testid={`store-product-row-${p.id}`} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-slate-900">{p.name} <span className="text-xs font-normal text-slate-400">harga dasar {fmt(p.price)}</span></p>
            <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
              <input data-testid={`store-online-${p.id}`} type="checkbox" checked={!!val(p, "online")} onChange={(e) => f(p.id, "online", e.target.checked)} className="w-4 h-4 accent-emerald-600" />
              Jual Online
            </label>
          </div>
          {p.online_photo_id && <img src={fileUrl(p.online_photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />}
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Harga Online</span>
              <input data-testid={`store-price-${p.id}`} type="number" min="0" value={val(p, "online_price")} onChange={(e) => f(p.id, "online_price", e.target.value)} className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Foto Produk</span>
              <label className="mt-0.5 h-10 border border-dashed border-slate-300 rounded-lg flex items-center justify-center gap-1.5 text-xs text-slate-500 cursor-pointer hover:bg-slate-50">
                <ImagePlus size={13} /> {(form[p.id] || {}).photo ? form[p.id].photo.name : "Pilih foto"}
                <input data-testid={`store-photo-${p.id}`} type="file" accept="image/*" hidden onChange={(e) => f(p.id, "photo", e.target.files[0])} />
              </label>
            </label>
          </div>
          <input data-testid={`store-desc-${p.id}`} value={val(p, "online_desc")} onChange={(e) => f(p.id, "online_desc", e.target.value)} placeholder="Deskripsi singkat untuk toko (opsional)" className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
          <button data-testid={`store-save-${p.id}`} onClick={() => save(p)} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center gap-1.5"><Save size={13} /> Simpan</button>
        </div>
      ))}
    </div>
  );
}

function OrdersTab({ user }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [resiFor, setResiFor] = useState(null);
  const [courier, setCourier] = useState("jnt");
  const [resiNo, setResiNo] = useState("");
  const [resiPhoto, setResiPhoto] = useState(null);
  const [scanning, setScanning] = useState(false);

  const scanBarcode = async (file) => {
    setResiPhoto(file);
    if (!file) return;
    setScanning(true);
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const result = await new BrowserMultiFormatReader().decodeFromImageUrl(URL.createObjectURL(file));
      setResiNo(result.getText().replace(/\s/g, "").toUpperCase());
      toast.success("Barcode terbaca otomatis");
    } catch {
      toast.info("Barcode tidak terbaca — ketik nomor resi manual");
    } finally { setScanning(false); }
  };

  const submitResi = async (o) => {
    if (!resiNo.trim()) return toast.error("Nomor resi wajib diisi");
    if (!resiPhoto) return toast.error("Foto barcode wajib diambil");
    const t = toast.loading("Menyimpan resi...");
    try {
      const fd = new FormData();
      fd.append("resi_no", resiNo.trim());
      fd.append("courier", courier);
      fd.append("photo", resiPhoto);
      const { data } = await api.post(`/store/orders/${o.id}/resi`, fd);
      toast.success(`Resi ${data.resi_no} tercatat — pembeli bisa melacak paketnya`, { id: t });
      setResiFor(null); setResiNo(""); setResiPhoto(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal simpan resi", { id: t }); }
  };
  const load = () => api.get("/store/orders" + (filter ? `?status=${filter}` : "")).then(({ data }) => {
    setRows(data);
    // sinkron status pembayaran aktif (fallback bila webhook Midtrans belum diset)
    const needSync = (data || []).filter((o) => o.midtrans_token && o.payment_status === "pending" && o.status !== "batal");
    if (needSync.length) {
      Promise.all(needSync.map((o) => api.get(`/store/orders/${o.code}/sync-payment`).catch(() => null)))
        .then(() => api.get("/store/orders" + (filter ? `?status=${filter}` : "")).then(({ data: d2 }) => setRows(d2)));
    }
  });
  useEffect(() => { load(); }, [filter]);

  const printSlip = async (o) => {
    const t = toast.loading("Membuat slip PDF...");
    try {
      const res = await api.get(`/store/orders/${o.id}/slip.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `slip-${o.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      const { data } = await api.post(`/store/orders/${o.id}/print-slip`);
      toast.success(data.status === "diproses" ? "Slip dicetak — pesanan DIPROSES KIRIM & notifikasi terkirim" : "Slip dicetak ulang", { id: t });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal membuat slip", { id: t }); }
  };

  const act = async (o, action) => {
    const t = toast.loading("Memproses...");
    try {
      const { data } = await api.post(`/store/orders/${o.id}/${action}`);
      toast.success(action === "confirm" ? "Pesanan dikonfirmasi — stok lokasi terpotong" : action === "complete" ? `Selesai${data.commission ? ` — komisi affiliate ${fmt(data.commission)} tercatat` : ""}` : "Pesanan dibatalkan & stok dikembalikan", { id: t });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {[["", "Semua"], ["pending", "Pending"], ["diproses", "Diproses"], ["selesai", "Selesai"], ["batal", "Batal"]].map(([v, l]) => (
          <button key={v} data-testid={`order-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`h-9 px-3 rounded-lg text-xs font-bold whitespace-nowrap ${filter === v ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {rows === null && <p className="text-sm text-slate-500">Memuat...</p>}
      {rows !== null && rows.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-sm text-slate-500">Belum ada pesanan.</div>}
      {(rows || []).map((o) => (
        <div key={o.id} data-testid={`store-order-${o.id}`} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono font-bold text-slate-900">{o.code}</span>
            <div className="flex gap-1.5 flex-wrap">
              <span data-testid={`order-status-${o.id}`} className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[o.status]}`}>{STATUS_LABEL[o.status]}</span>
              {o.payment_status && (
                <span data-testid={`order-payment-${o.id}`} className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : o.payment_status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  {o.payment_status === "paid" ? "LUNAS" : o.payment_status === "failed" ? "GAGAL BAYAR" : "BELUM BAYAR"}
                </span>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-700"><b>{o.customer_name}</b> · {o.phone}</p>
          <p className="text-xs text-slate-500">{o.address}{o.city ? ` — ${o.city}${o.province ? `, ${o.province}` : ""}` : ""}</p>
          <p className="text-xs text-slate-500">Kirim dari <b>{o.location_name}</b> · {new Date(o.created_at).toLocaleString("id-ID")}</p>
          <div className="bg-slate-50 rounded-lg p-2.5 space-y-0.5">
            {o.items.map((it, i) => (
              <div key={i} className="flex justify-between text-xs"><span className="text-slate-700">{it.name} × {it.qty}</span><span className="font-semibold">{fmt(it.subtotal)}</span></div>
            ))}
            {o.shipping_cost !== undefined && (
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-700">Ongkir (JNT)</span>
                {o.free_shipping ? (
                  <span data-testid={`order-free-shipping-${o.id}`} className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-extrabold">GRATIS</span>
                ) : (
                  <span className="font-semibold">{fmt(o.shipping_cost)}</span>
                )}
              </div>
            )}
            <div className="flex justify-between text-xs font-bold pt-1 border-t border-slate-200"><span>Total</span><span>{fmt(o.total)}</span></div>
          </div>
          {o.affiliate_code && <p className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-2.5 py-1.5">via affiliate <b>{o.affiliate_name}</b> ({o.affiliate_code}){o.commission_amount ? ` · komisi ${fmt(o.commission_amount)}` : ""}</p>}
          {o.printed_by && <p className="text-[11px] text-slate-400">Slip diprint oleh <b>{o.printed_by}</b> · {new Date(o.printed_at).toLocaleString("id-ID")}</p>}
          {o.resi_no && (
            <p data-testid={`order-resi-${o.id}`} className="text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5">
              Resi {o.resi_no} · {o.courier?.toUpperCase()} · diinput {o.resi_by}
            </p>
          )}
          {user.role === "admin" ? (
            <p data-testid={`order-monitor-note-${o.id}`} className="text-[11px] text-slate-400 pt-1 italic">Mode pantau owner — operasional (print slip, resi, selesai) dilakukan karyawan {o.location_name}.</p>
          ) : (
          <div className="flex gap-2 pt-1 flex-wrap">
            {o.status === "diproses" && (
              <button data-testid={`order-resi-btn-${o.id}`} onClick={() => { setResiFor(resiFor === o.id ? null : o.id); setResiNo(""); setResiPhoto(null); setCourier("jnt"); }} className="h-10 px-4 rounded-lg border border-blue-400 text-blue-700 text-xs font-bold flex items-center gap-1.5 active:scale-[0.98] transition-transform"><Package size={13} /> {o.resi_no ? "Ubah Resi" : "Input Resi"}</button>
            )}
            {(o.status === "pending" || o.status === "diproses") && o.payment_status === "paid" && (
              <button data-testid={`order-print-${o.id}`} onClick={() => printSlip(o)} className="h-10 px-4 rounded-lg bg-black text-[#FFCC00] text-xs font-extrabold flex items-center gap-1.5 active:scale-[0.98] transition-transform"><Printer size={13} /> {o.printed_by ? "Print Ulang" : o.status === "pending" ? "Print & Proses Kirim" : "Print Slip"}</button>
            )}
            {o.status === "pending" && (
              <button data-testid={`order-confirm-${o.id}`} onClick={() => act(o, "confirm")} className="flex-1 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold">Konfirmasi & Potong Stok</button>
            )}
            {o.status === "diproses" && (
              <button data-testid={`order-complete-${o.id}`} onClick={() => act(o, "complete")} className="flex-1 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold">Tandai Selesai</button>
            )}
            {(o.status === "pending" || o.status === "diproses") && (
              <button data-testid={`order-cancel-${o.id}`} onClick={() => act(o, "cancel")} className="h-10 px-4 rounded-lg border border-red-300 text-red-600 text-xs font-bold">Batalkan</button>
            )}
          </div>
          )}
          {resiFor === o.id && (
            <div data-testid={`resi-form-${o.id}`} className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 space-y-2.5">
              <p className="text-xs font-extrabold text-blue-900">Input Resi Kurir</p>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Kurir</span>
                <select data-testid={`resi-courier-${o.id}`} value={courier} onChange={(e) => setCourier(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg border border-blue-300 text-sm bg-white">
                  {[["jnt", "J&T Express (JNT)"], ["jne", "JNE"], ["sicepat", "SiCepat"], ["anteraja", "AnterAja"], ["ninja", "Ninja Xpress"], ["spx", "Shopee Express"], ["pos", "POS Indonesia"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">1. Foto barcode pada paket</span>
                <input data-testid={`resi-photo-${o.id}`} type="file" accept="image/*" capture="environment" onChange={(e) => scanBarcode(e.target.files[0])} className="mt-1 w-full text-xs" />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">2. Nomor resi {scanning ? "(membaca...)" : "(terisi otomatis dari barcode, bisa dikoreksi)"}</span>
                <input data-testid={`resi-no-${o.id}`} value={resiNo} onChange={(e) => setResiNo(e.target.value.toUpperCase())} placeholder="Contoh: 882000123456" className="mt-1 w-full h-11 px-3 rounded-lg border border-blue-300 text-sm font-mono" />
              </label>
              <button data-testid={`resi-submit-${o.id}`} onClick={() => submitResi(o)} className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-extrabold active:scale-[0.98] transition-transform">Simpan Resi — Pembeli Bisa Lacak</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CustomersTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/store/customers").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  return (
    <div className="space-y-3">
      {rows === null && <p className="text-sm text-slate-500">Memuat...</p>}
      {rows !== null && rows.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-sm text-slate-500">Belum ada pembeli. Data muncul otomatis setelah ada pesanan toko online.</div>}
      {(rows || []).map((c) => (
        <div key={c.phone} data-testid={`customer-${c.phone}`} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-slate-900">{c.name}</p>
            <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold uppercase tracking-wider">{c.orders} order</span>
          </div>
          <p className="text-sm text-slate-600 mt-0.5">{c.phone}</p>
          <p className="text-xs text-slate-500 mt-0.5">{c.address}</p>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-slate-400">Terakhir order {c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("id-ID") : "-"}</span>
            <span className="font-extrabold text-emerald-700" style={{fontFamily:'Manrope'}}>Total belanja {fmt(c.total_spent)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AffiliatesTab({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [pct, setPct] = useState(5);
  const load = () => api.get("/store/affiliates").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/store/settings").then(({ data }) => setPct(data.commission_pct)); }, []);

  const savePct = async () => {
    const t = toast.loading("Menyimpan...");
    try {
      await api.put("/store/settings", { commission_pct: parseFloat(pct) });
      toast.success("Komisi affiliate diperbarui", { id: t });
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-2">
          <label className="flex-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Komisi Affiliate (% dari nilai order)</span>
            <input data-testid="commission-pct" type="number" min="0" max="50" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
          </label>
          <button data-testid="commission-save" onClick={savePct} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center gap-1"><Settings2 size={13} /> Simpan</button>
        </div>
      )}
      {rows === null && <p className="text-sm text-slate-500">Memuat...</p>}
      {rows !== null && rows.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-sm text-slate-500">Belum ada affiliate. Bagikan link pendaftaran: halaman Toko → tab Affiliate.</div>}
      {(rows || []).map((a) => (
        <div key={a.id} data-testid={`affiliate-row-${a.code}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-slate-900 truncate">{a.name} <span className="font-mono text-xs text-purple-700">{a.code}</span></p>
            <p className="text-xs text-slate-500">{a.phone} · {a.orders} order · omset {fmt(a.omset)}</p>
          </div>
          <p className="text-sm font-extrabold text-emerald-700 flex-shrink-0">{fmt(a.komisi)}</p>
        </div>
      ))}
    </div>
  );
}

function ShippingTab() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/store/settings"), fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/shipping-info`).then((r) => r.json())])
      .then(([{ data }, pub]) => {
        setInfo(pub);
        setForm({ gudang_province: data.gudang_province || "", gudang_city: data.gudang_city || "",
                  free_same_city_min: data.free_same_city_min, free_same_province_min: data.free_same_province_min, flat_rate: data.flat_rate });
      });
  }, []);

  const save = async () => {
    const t = toast.loading("Menyimpan...");
    try {
      await api.put("/store/settings", {
        gudang_province: form.gudang_province, gudang_city: form.gudang_city,
        free_same_city_min: parseFloat(form.free_same_city_min) || 0,
        free_same_province_min: parseFloat(form.free_same_province_min) || 0,
        flat_rate: parseFloat(form.flat_rate) || 0,
      });
      toast.success("Pengaturan ongkir disimpan", { id: t });
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  if (!form) return <p className="text-sm text-slate-500">Memuat...</p>;
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="font-bold text-slate-900">Lokasi Gudang Pusat</h3>
        <p className="text-xs text-slate-500">Dipakai untuk menghitung gratis ongkir. Gudang Pusat di Jabodetabek melayani gratis ongkir seluruh Jabodetabek.</p>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Provinsi</span>
            <select data-testid="ship-gudang-province" value={form.gudang_province} onChange={(e) => setForm({ ...form, gudang_province: e.target.value })} className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="">Pilih provinsi</option>
              {(info?.provinces || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Kota</span>
            <input data-testid="ship-gudang-city" value={form.gudang_city} onChange={(e) => setForm({ ...form, gudang_city: e.target.value })} placeholder="Contoh: Jakarta" className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
          </label>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="font-bold text-slate-900">Aturan Ongkir JNT</h3>
        <div className="grid grid-cols-1 gap-2">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Min. order gratis ongkir — satu kota / Jabodetabek (Rp)</span>
            <input data-testid="ship-min-city" type="number" min="0" value={form.free_same_city_min} onChange={(e) => setForm({ ...form, free_same_city_min: e.target.value })} className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Min. order gratis ongkir — satu provinsi (Rp)</span>
            <input data-testid="ship-min-province" type="number" min="0" value={form.free_same_province_min} onChange={(e) => setForm({ ...form, free_same_province_min: e.target.value })} className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tarif flat di bawah ambang (Rp)</span>
            <input data-testid="ship-flat-rate" type="number" min="0" value={form.flat_rate} onChange={(e) => setForm({ ...form, flat_rate: e.target.value })} className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm" />
          </label>
        </div>
        <p className="text-[11px] text-slate-400">Pembeli dari provinsi tanpa gudang/cabang tidak bisa checkout dan melihat pesan "Tunggu gudang kami hadir di kota Anda". Atur provinsi tiap cabang di menu Cabang.</p>
        <button data-testid="ship-save" onClick={save} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center gap-1.5"><Save size={13} /> Simpan</button>
      </div>
    </div>
  );
}

export default function StoreAdmin() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "orders";
  const setTab = (t) => setSearchParams({ tab: t });
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Penjualan Online</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Toko Online</h1>
        <p className="text-sm text-slate-600 mt-1">Kelola produk toko, konfirmasi pesanan masuk (stok terpotong saat konfirmasi), dan pantau affiliate.</p>
      </div>
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto no-scrollbar">
        {[["orders", "Pesanan", ClipboardList], ["customers", "Pembeli", ContactRound], ["products", "Produk Toko", Package], ["affiliates", "Affiliate", Users], ["ongkir", "Ongkir", Truck]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`storeadmin-tab-${id}`} onClick={() => setTab(id)} className={`flex-1 h-10 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 whitespace-nowrap px-3 ${tab === id ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>
      {tab === "orders" && <OrdersTab user={user} />}
      {tab === "customers" && (["admin", "keuangan"].includes(user?.role) ? <CustomersTab /> : <p className="text-sm text-slate-500">Data pembeli hanya untuk admin & keuangan.</p>)}
      {tab === "products" && (user?.role === "admin" ? <ProductsTab /> : <p className="text-sm text-slate-500">Hanya admin yang dapat mengatur produk toko.</p>)}
      {tab === "affiliates" && <AffiliatesTab isAdmin={user?.role === "admin"} />}
      {tab === "ongkir" && (user?.role === "admin" ? <ShippingTab /> : <p className="text-sm text-slate-500">Hanya admin yang dapat mengatur ongkir.</p>)}
    </div>
  );
}
